import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Search, Users, Clock, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [queries, setQueries] = useState([]);
  const [stats, setStats] = useState({ total_queries: 0, new: 0, contacted: 0, closed: 0, partners: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([api.get("/queries"), api.get("/stats")]);
      setQueries(q.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load queries");
    } finally {
      setLoading(false);
    }
  };

  const loadBanks = async () => {
    setBanksLoading(true);
    try {
      const { data } = await api.get("/banks");
      setBanks(data);
    } catch (e) {
      toast.error("Failed to load lender rates");
    } finally {
      setBanksLoading(false);
    }
  };

  useEffect(() => { load(); loadBanks(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/queries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const filtered = queries.filter(q => {
    if (filter !== "all" && q.status !== filter) return false;
    if (search && !`${q.name} ${q.email} ${q.phone}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const statusBadge = (s) => {
    const map = {
      new: "bg-emerald-100 text-emerald-700 border-emerald-200",
      contacted: "bg-blue-100 text-blue-700 border-blue-200",
      closed: "bg-slate-100 text-slate-600 border-slate-200",
    };
    return <Badge className={`${map[s]} border font-semibold`}>{s.toUpperCase()}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <Header onCTA={() => window.location.href = "/#contact"} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Admin Dashboard</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900">Loan Enquiries</h1>
            <p className="mt-1.5 text-sm text-slate-600">Every query submitted through the site lands here in real time.</p>
          </div>
          <Button data-testid="admin-refresh" onClick={load} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* Stats bento */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={<Users className="w-5 h-5" />} label="Total Queries" value={stats.total_queries} testId="stat-total" />
          <StatCard icon={<Clock className="w-5 h-5" />} label="New" value={stats.new} accent testId="stat-new" />
          <StatCard icon={<CheckCircle className="w-5 h-5" />} label="Contacted" value={stats.contacted} testId="stat-contacted" />
          <StatCard icon={<XCircle className="w-5 h-5" />} label="Closed" value={stats.closed} testId="stat-closed" />
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or phone"
              className="pl-9 bg-white h-10"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-filter" className="w-40 bg-white h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="mt-6 border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <Table data-testid="admin-queries-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Loan</TableHead>
                  <TableHead>Bank</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Loading...</TableCell></TableRow>
                )}
                {!loading && filtered.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-16 text-slate-500">
                    No queries yet. Submit one from the landing page to see it here.
                  </TableCell></TableRow>
                )}
                {!loading && filtered.map((q) => (
                  <TableRow key={q.id} data-testid={`admin-row-${q.id}`}>
                    <TableCell className="font-medium text-slate-900">{q.name}</TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-900">{q.phone}</div>
                      <div className="text-xs text-slate-500">{q.email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-900">{formatINR(q.loan_amount)}</div>
                      <div className="text-xs text-slate-500">{q.tenure_months || "—"} months</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{q.bank_interested || "Any"}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[200px] truncate" title={q.message}>{q.message || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                      {new Date(q.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </TableCell>
                    <TableCell>{statusBadge(q.status)}</TableCell>
                    <TableCell className="text-right">
                      <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                        <SelectTrigger data-testid={`admin-status-${q.id}`} className="w-32 h-8 text-xs bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Lender rate management */}
        <div className="mt-12 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-2xl font-semibold text-slate-900">Manage Lender Rates</h2>
            <p className="mt-1 text-sm text-slate-600">
              Update interest rates and fees here whenever a bank changes theirs — no code changes needed.
            </p>
          </div>
          <Button data-testid="admin-banks-refresh" onClick={loadBanks} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${banksLoading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <Card className="mt-4 border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <BanksTable banks={banks} loading={banksLoading} onSaved={loadBanks} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const BanksTable = ({ banks, loading, onSaved }) => {
  const [editing, setEditing] = useState(null); // bank id being edited
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = (b) => {
    setEditing(b.id);
    setDraft({
      interest_rate_min: b.interest_rate_min,
      interest_rate_max: b.interest_rate_max,
      processing_fee_min: b.processing_fee_min,
      processing_fee_max: b.processing_fee_max,
    });
  };

  const cancel = () => { setEditing(null); setDraft({}); };

  const save = async (id) => {
    setSaving(true);
    try {
      await api.patch(`/banks/${id}`, {
        interest_rate_min: Number(draft.interest_rate_min),
        interest_rate_max: Number(draft.interest_rate_max),
        processing_fee_min: Number(draft.processing_fee_min),
        processing_fee_max: Number(draft.processing_fee_max),
      });
      toast.success("Lender rates updated");
      cancel();
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update rates");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Table data-testid="admin-banks-table">
      <TableHeader>
        <TableRow className="bg-slate-50">
          <TableHead>Lender</TableHead>
          <TableHead>Interest Rate (%)</TableHead>
          <TableHead>Processing Fee (%)</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && (
          <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-500">Loading...</TableCell></TableRow>
        )}
        {!loading && banks.map((b) => {
          const isEditing = editing === b.id;
          return (
            <TableRow key={b.id} data-testid={`admin-bank-row-${b.id}`}>
              <TableCell className="font-medium text-slate-900">{b.name}</TableCell>
              <TableCell>
                {isEditing ? (
                  <div className="flex items-center gap-1.5">
                    <Input type="number" step="0.01" className="w-20 h-8 bg-white" value={draft.interest_rate_min}
                      onChange={(e) => setDraft(d => ({ ...d, interest_rate_min: e.target.value }))} />
                    <span className="text-slate-400 text-xs">to</span>
                    <Input type="number" step="0.01" className="w-20 h-8 bg-white" value={draft.interest_rate_max}
                      onChange={(e) => setDraft(d => ({ ...d, interest_rate_max: e.target.value }))} />
                  </div>
                ) : (
                  <span className="text-sm text-slate-700">{b.interest_rate_min}% – {b.interest_rate_max}%</span>
                )}
              </TableCell>
              <TableCell>
                {isEditing ? (
                  <div className="flex items-center gap-1.5">
                    <Input type="number" step="0.01" className="w-20 h-8 bg-white" value={draft.processing_fee_min}
                      onChange={(e) => setDraft(d => ({ ...d, processing_fee_min: e.target.value }))} />
                    <span className="text-slate-400 text-xs">to</span>
                    <Input type="number" step="0.01" className="w-20 h-8 bg-white" value={draft.processing_fee_max}
                      onChange={(e) => setDraft(d => ({ ...d, processing_fee_max: e.target.value }))} />
                  </div>
                ) : (
                  <span className="text-sm text-slate-700">{b.processing_fee_min}% – {b.processing_fee_max}%</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                {isEditing ? (
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={cancel} disabled={saving}>Cancel</Button>
                    <Button size="sm" onClick={() => save(b.id)} disabled={saving}>
                      {saving ? "Saving..." : "Save"}
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => startEdit(b)}>Edit</Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const StatCard = ({ icon, label, value, accent, testId }) => (
  <Card className={`border-slate-200 ${accent ? "bg-slate-900 text-white border-slate-900" : "bg-white"}`}>
    <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
      <CardTitle className={`text-xs font-bold uppercase tracking-[0.15em] ${accent ? "text-emerald-400" : "text-slate-500"}`}>{label}</CardTitle>
      <span className={accent ? "text-emerald-400" : "text-slate-400"}>{icon}</span>
    </CardHeader>
    <CardContent>
      <div className={`font-display font-semibold text-3xl ${accent ? "text-white" : "text-slate-900"}`} data-testid={testId}>{value}</div>
    </CardContent>
  </Card>
);
